// React.
import { useContext, useMemo, useState } from 'react';

// Types.
import { BlockingSessionTime } from '../../../types/instance/BlockingSession';

// Components.
import EChartsWithData from '../../chart/echarts/EChartsWithData';
import TimeSeriesChart from '../../chart/echarts/TimeSeriesChart';
import { EChartPoint, EChartTimeSeries } from '../../chart/echarts/types';

// Helpers.
import { getConvertedTimeToUTC } from '../../../helpers/utils';
import { buildApiQuery } from '../../../helpers/timeRangeUtils';

// Context
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE } from "../../Constants";
import BlockingSessionApi from '../../../api/instance/BlockingSessionApi';

const CHART_MODE = {
    BLOCKING_TIME: 0,
    BLOCKED_SESSIONS: 1,
};

const chartModeOptions = [
    { label: 'Blocking Time', value: CHART_MODE.BLOCKING_TIME },
    { label: 'Blocked Sessions', value: CHART_MODE.BLOCKED_SESSIONS },
];

const durationAxis = [{ labels: true, name: 'Duration', format: 'time' as const }];
const blockedAxis = [{ labels: true, name: 'Blocked Time', format: 'time' as const }];

export default function BlockingSessionsChart(props: {
    error: string | null,
    loading: boolean,
    data: BlockingSessionTime[],
    blockingList: any[],
    instanceId: number,
}) {
    const { error, loading, data, blockingList, instanceId } = props;
    const timeRangeContext = useContext(TimeRangeContext);
    const [chartMode, setChartMode] = useState<number>(CHART_MODE.BLOCKING_TIME);
    const [sessionSeries, setSessionSeries] = useState<EChartTimeSeries[]>([]);
    const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);

    const blockedSessions = useMemo(() => {
        const sessions: any[] = [];
        blockingList.forEach(blocker => {
            if (blocker.subRows?.length) {
                blocker.subRows.forEach((blocked: any) => {
                    if (blocked.session && !sessions.find(s => s.session === blocked.session)) {
                        sessions.push(blocked);
                    }
                });
            }
        });
        return sessions.slice(0, 20);
    }, [blockingList]);

    void useMemo(async () => {
        if (chartMode !== CHART_MODE.BLOCKED_SESSIONS || blockedSessions.length === 0) {
            setSessionSeries([]);
            return;
        }
        setSessionsLoading(true);

        const promises = blockedSessions.map(session =>
            BlockingSessionApi.fetchSessionTime(buildApiQuery(timeRangeContext.timeRange), instanceId, session.session)
                .then((res: Response) => res.json())
                .catch(() => [])
        );

        const results = await Promise.all(promises);

        const series: EChartTimeSeries[] = results.map((timeData: any[], index) => ({
            name: blockedSessions[index].session,
            color: CHART_COLOURS_STATEMENTS[index],
            chartType: 'bar' as const,
            data: Array.isArray(timeData)
                ? timeData.map((item: BlockingSessionTime): EChartPoint => [getConvertedTimeToUTC(item), item.waittime])
                : [],
        }));

        setSessionSeries(series);
        setSessionsLoading(false);
    }, [chartMode, blockedSessions, timeRangeContext]);

    const blockingTimeSeries = useMemo<EChartTimeSeries[]>(() => [{
        name: 'Blocking Time',
        chartType: 'bar',
        data: data.map((item): EChartPoint => [getConvertedTimeToUTC(item), item['waittime']]),
    }], [data]);

    const selectedLabel = chartModeOptions.find(o => o.value === chartMode)?.label ?? 'Blocking Time';
    const isLoadingChart = loading || (chartMode === CHART_MODE.BLOCKED_SESSIONS && sessionsLoading);

    return (
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                Blocking time
                <div className="float-end d-inline-flex align-items-center gap-1">
                    <div className="dropdown">
                        <button id="btnBlockingChartMode" type="button"
                                className="btn btn-xsm btn-primary dropdown-toggle"
                                data-bs-toggle="dropdown" aria-expanded="false">
                            {selectedLabel}
                        </button>
                        <ul className="dropdown-menu border" aria-labelledby="btnBlockingChartMode">
                            <li>
                                {chartModeOptions.map(({ label, value }) => (
                                    <span
                                        key={value}
                                        className={`dropdown-item ${chartMode === value ? 'active' : ''}`}
                                        role="button"
                                        onClick={() => setChartMode(value)}
                                    >
                                        {label}
                                    </span>
                                ))}
                            </li>
                        </ul>
                    </div>
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapse-space" aria-expanded="false"
                       aria-controls="collapse-space"/>
                </div>
            </div>
            <div id="collapse-space" className="card-body collapse show">
                {chartMode === CHART_MODE.BLOCKING_TIME && (
                    <EChartsWithData error={error} loading={isLoadingChart} length={data.length}
                                     series={blockingTimeSeries} tooltipType={CHART_DATA_TYPE.TIME}
                                     legend={true} yAxis={durationAxis}/>
                )}
                {chartMode === CHART_MODE.BLOCKED_SESSIONS && (
                    isLoadingChart
                        ? <EChartsWithData error={null} loading={true} length={0}
                                           series={blockingTimeSeries} tooltipType={CHART_DATA_TYPE.TIME}/>
                        : sessionSeries.length > 0
                            ? <TimeSeriesChart series={sessionSeries} tooltipType={CHART_DATA_TYPE.TIME}
                                               legend={true} yAxis={blockedAxis}/>
                            : <EChartsWithData error={error} loading={false} length={0}
                                               series={blockingTimeSeries} tooltipType={CHART_DATA_TYPE.TIME}/>
                )}
            </div>
        </div>
    );
}
